const {
  debounce,
  getElement,
  getElements,
  getViewPercentage,
  viewportSize,
} = window.BAO.Utils

export class GridSwitcher extends window.BAO.CustomElement() {
  constructor () {
    super()

    document.addEventListener(
      'DOMContentLoaded',
      this.handleFiltersOnLoad.bind(this),
    )
  }

  connectedCallback () {
    super.connectedCallback()

    this.loading = false

    // If the collection has editorial is an option, we ignore the saved column values
    const shouldUseEditorial = this.editorialViewEnabled && !this.hidePromos

    this.columnsMobile = shouldUseEditorial
      ? 2
      : Number(this.dataset.columnsMobile)
    this.columnsDesktop = shouldUseEditorial
      ? 4
      : Number(this.dataset.columnsDesktop)

    this.hideLifestyleMobile = this.hidePromos
      ? true
      : shouldUseEditorial
        ? false
        : this.dataset.hideLifestyleMobile === 'true'
    this.hideLifestyleDesktop = this.hidePromos
      ? true
      : shouldUseEditorial
        ? false
        : this.dataset.hideLifestyleDesktop === 'true'
    this.editorialViewActive =
      this.dataset.editorialViewActive === 'true' && !this.hidePromos

    if (this.mergeLifestyleBlocks) {
      this.hideLifestyleMobile = false
      this.hideLifestyleDesktop = false
      const listEl = this.prdListElem
      if (listEl) {
        listEl.setAttribute('data-hide-lifestyle-mobile', 'false')
        listEl.setAttribute('data-hide-lifestyle-desktop', 'false')
      }
    }

    this.updateProductContentSpacing()

    this.filterOptionClick = this.filterOptionClick.bind(this)
  }

  setupListeners () {
    super.setupListeners()

    if (this.els?.next?.exists) {
      this.onNextClick = this.onNextClick.bind(this)
      this.listeners.add(this.els.next.element, 'click', this.onNextClick)
    }

    if (this.els?.button?.exists) {
      this.onButtonClick = this.onButtonClick.bind(this)

      this.els.button.elements.forEach(buttonEl => {
        this.listeners.add(buttonEl, 'click', this.onButtonClick)
      })
    }

    // Grid view button (found in Editorial section)
    this.onGridViewButtonClick = this.onGridViewButtonClick.bind(this)
    this.listeners.add(document.body, 'click', e => {
      const gridViewButton = e.target.closest(
        '[data-grid-switcher-external-el="grid-view"]',
      )
      if (gridViewButton) this.onGridViewButtonClick()
    })

    // Filtering logic
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this)
    this.onFilterResetClick = this.onFilterResetClick.bind(this)

    if (this.externalEls.reset?.exists && this.editorialViewActive) {
      this.listeners.add(
        this.externalEls.reset.element,
        'click',
        this.onFilterResetClick,
      )
    }

    // Detect filters open when in Editorial mode
    if (
      this.externalEls['filter-button']?.element &&
      this.dataset.editorialViewActive === 'true'
    ) {
      this.listeners.add(
        this.externalEls['filter-button'].element,
        'click',
        this.onFilterButtonClick,
      )
    }

    this.onCollectionGridMutated = this.onCollectionGridMutated.bind(this)
    this.listeners.add(
      document,
      window.BAO.EVENTS.LOAD_MORE.FETCHED_NEXT_PAGE,
      this.onCollectionGridMutated,
    )
    this.listeners.add(
      document,
      window.BAO.EVENTS.LOAD_MORE.FETCHED_PREV_PAGE,
      this.onCollectionGridMutated,
    )
    this.listeners.add(
      document,
      'collectionRendered',
      this.onCollectionGridMutated,
    )

    this.onResize = debounce(this.onCollectionGridMutated, 120)
    this.listeners.add(window, 'resize', this.onResize)
  }

  handleFiltersOnLoad () {
    // If the page is loaded in editorial mode, but with active filters in the URL, cycle columns
    if (this.editorialViewActive && this.hasFilterParams) {
      this.cycleColumns()
    }
  }

  updateProductContentSpacing () {
    const listElement = this.prdListElem
    if (!listElement) return

    const productContent = Array.from(
      listElement.querySelectorAll(
        '[data-load-more-list-el="item"] [data-grid-switcher-external-el="product-card-content"]',
      ),
    )

    if (productContent.length === 0) return

    productContent.forEach(card => {
      card.classList.remove('prd-Card_Content-primarycolumn')
    })

    const visibleGridItems = this.getVisibleGridItems(listElement)
    const visibleProducts = visibleGridItems.filter(item => item.isProduct)

    visibleProducts.forEach(product => {
      const nearestLeftItem = this.getNearestLeftGridItem(
        product,
        visibleGridItems,
      )

      if (!nearestLeftItem || nearestLeftItem.isPromo) {
        product.contentElement?.classList.add('prd-Card_Content-primarycolumn')
      }
    })
  }

  onCollectionGridMutated () {
    this.updateProductContentSpacing()
  }

  getVisibleGridItems (listElement) {
    return Array.from(listElement.children)
      .map(item => {
        const loadMoreItemType = item.dataset.loadMoreListEl
        const isProduct = loadMoreItemType === 'item'
        const isPromo = loadMoreItemType === 'lifestyle-block'

        if (!isProduct && !isPromo) return null
        if (!this.isGridItemVisible(item, isPromo, listElement)) return null

        const left = item.offsetLeft
        const top = item.offsetTop

        return {
          element: item,
          isProduct,
          isPromo,
          contentElement: isProduct
            ? item.querySelector(
              '[data-grid-switcher-external-el="product-card-content"]',
            )
            : null,
          left,
          right: left + item.offsetWidth,
          top,
          bottom: top + item.offsetHeight,
        }
      })
      .filter(Boolean)
  }

  isGridItemVisible (item, isPromo, listElement) {
    const isExplicitlyHidden =
      item.hidden || item.getAttribute('aria-hidden') === 'true'

    if (isExplicitlyHidden) {
      return false
    }

    if (!isPromo) {
      return true
    }

    const isMobileViewport = viewportSize() === 'xs'
    const hideLifestyleForViewport = isMobileViewport
      ? listElement.dataset.hideLifestyleMobile === 'true'
      : listElement.dataset.hideLifestyleDesktop === 'true'

    const isMobileOnlyPromo = item.classList.contains(
      'prd-List_LifestyleBlock-mobile',
    )
    const isDesktopOnlyPromo = item.classList.contains(
      'prd-List_LifestyleBlock-desktop',
    )
    const isDesktopSixColumnLayout =
      !isMobileViewport && Number(listElement.dataset.columnsDesktop) === 6

    const hiddenByLayout =
      this.hidePromos || hideLifestyleForViewport || isDesktopSixColumnLayout
    const hiddenByViewport = isMobileViewport
      ? isDesktopOnlyPromo
      : isMobileOnlyPromo

    return !hiddenByLayout && !hiddenByViewport
  }

  getNearestLeftGridItem (targetItem, gridItems) {
    const HORIZONTAL_TOLERANCE = 1
    const VERTICAL_OVERLAP_TOLERANCE = 1
    let nearestItem = null

    gridItems.forEach(candidate => {
      if (candidate.element === targetItem.element) return

      const isLeftOfTarget =
        candidate.right <= targetItem.left + HORIZONTAL_TOLERANCE
      if (!isLeftOfTarget) return

      const verticalOverlap =
        Math.min(candidate.bottom, targetItem.bottom) -
        Math.max(candidate.top, targetItem.top)

      if (verticalOverlap <= VERTICAL_OVERLAP_TOLERANCE) return

      if (!nearestItem || candidate.right > nearestItem.right) {
        nearestItem = candidate
      }
    })

    return nearestItem
  }

  onNextClick () {
    if (this.loading) return

    window.dataLayer.push({
      event: 'gridSwitcherButtonClick',
      button: this.els.next.element.dataset.title,
    })

    this.cycleColumns()
  }

  onButtonClick (e) {
    if (this.loading) return

    const buttonEl = e.target.closest(this.els.button.selector)
    if (!buttonEl) return

    this.viewport = viewportSize() === 'xs' ? 'mobile' : 'desktop'

    const editorial = buttonEl.dataset.editorial === 'true'
    const lifestyle = buttonEl.dataset.lifestyle === 'true'
    const columns = buttonEl.dataset.columns

    const forcedNextCycle = {
      editorial,
      lifestyle,
      columns,
    }

    this.cycleColumns(forcedNextCycle)

    this.updateButtons(buttonEl)
  }

  onGridViewButtonClick () {
    if (this.loading) return

    this.cycleColumns()
  }

  async cycleColumns (forcedNextCycle = null) {
    this.setLoading()
    this.updateCycles()
    this.viewport = viewportSize() === 'xs' ? 'mobile' : 'desktop'

    this.getInViewCard()

    const currentCycle = this.currentCycle()
    const nextCycle = forcedNextCycle || this.nextCycle()

    if (!currentCycle || !nextCycle) {
      console.warn(
        'Current or next cycle can be found. Please check buildMobileCycles and buildDesktopCycles logic.',
      )
      this.unsetLoading()
      return
    }

    const switchToEditorial = !currentCycle.editorial && nextCycle.editorial
    const switchToGrid = currentCycle.editorial && !nextCycle.editorial
    const cycleGrid = !currentCycle.editorial && !nextCycle.editorial

    const { columns, lifestyle } = nextCycle

    if (switchToEditorial) {
      // If an editorial view is present on the current collection, it will always be the default
      // on load, therefore we don't need to update any values
      await this.switchToEditorial()
    } else if (switchToGrid) {
      // When loading the grid, we set the column storage first so there's no layout shift once loaded
      this.updateProperties(columns, lifestyle)
      this.setColumnStorage(columns, lifestyle)

      await this.switchToGrid()
    } else if (cycleGrid) {
      this.setColumnStorage(columns, lifestyle)
      this.updateProperties(columns, lifestyle)
      this.updateGridStyles(columns, lifestyle)
      this.updateTriggerIcon(columns, lifestyle)
      this.scrollToInViewCard()
    }

    this.updateProductContentSpacing()

    this.unsetLoading()

    if (switchToEditorial || switchToGrid) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.GRID_SWITCHER.SWITCHED_LAYOUT)
    } else {
      window.BAO.dispatchEvent(window.BAO.EVENTS.GRID_SWITCHER.SWITCHED_COLUMNS)
    }
  }

  updateProperties (columns, lifestyle) {
    if (this.viewport === 'mobile') {
      this.columnsMobile = Number(columns)

      if (this.lifestyleViewEnabled) {
        this.hideLifestyleMobile = !lifestyle
      }
    }
    if (this.viewport === 'desktop') {
      this.columnsDesktop = Number(columns)

      if (this.lifestyleViewEnabled) {
        this.hideLifestyleDesktop = !lifestyle
      }
    }
  }

  updateGridStyles (columns, lifestyle) {
    const columnStyleVariable =
      this.viewport === 'mobile'
        ? '--Grid_ColumnsMobile'
        : '--Grid_ColumnsDesktop'
    const columnAttribute =
      this.viewport === 'mobile'
        ? 'data-columns-mobile'
        : 'data-columns-desktop'
    const hideLifestyleAttribute =
      this.viewport === 'mobile'
        ? 'data-hide-lifestyle-mobile'
        : 'data-hide-lifestyle-desktop'

    // Update to use desktop and mobile independently
    this.prdListElem?.style.setProperty(columnStyleVariable, Number(columns))
    this.prdListElem?.setAttribute(columnAttribute, Number(columns))
    this.prdListElem?.setAttribute(hideLifestyleAttribute, !lifestyle)
  }

  updateTriggerIcon (columns, lifestyle) {
    this.els.icon?.elements.forEach(icon => {
      const iconLifestyle = icon.dataset.lifestyle === 'true'

      if (icon.dataset.viewport === this.viewport) {
        if (
          Number(icon.dataset.columns) === Number(columns) &&
          iconLifestyle === lifestyle
        ) {
          icon.setAttribute('aria-hidden', false)
        } else {
          icon.setAttribute('aria-hidden', true)
        }
      }
    })
  }

  async setColumnStorage (value, lifestyle) {
    const columnStorageName =
      this.viewport === 'mobile'
        ? this.storageNameColumnsMobile
        : this.storageNameColumnsDesktop
    const hideLifestyleStorageName =
      this.viewport === 'mobile'
        ? this.storageNameHideLifestyleMobile
        : this.storageNameHideLifestyleDesktop

    const attributes = {
      [columnStorageName]: Number(value),
    }

    if (this.lifestyleViewEnabled && !this.mergeLifestyleBlocks) {
      attributes[hideLifestyleStorageName] = !lifestyle
    }

    // eslint-disable-next-line compat/compat
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    })
  }

  getInViewCard () {
    const listItemsArray = getElements('.prd-List_Items > .prd-List_Item')

    const offsetTopArray = listItemsArray.map(listItem => {
      return listItem.getBoundingClientRect().top
    })

    const closestToTop = this.closestToZero(offsetTopArray)
    this.closestCardIndex = offsetTopArray.indexOf(closestToTop)
  }

  scrollToInViewCard () {
    if (!this.closestCardIndex) return

    const listItems = document.querySelectorAll(
      '.prd-List_Items > .prd-List_Item',
    )
    const topListItem = listItems[this.closestCardIndex]

    if (!topListItem) return

    if (getViewPercentage(topListItem) < 70) {
      topListItem.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  closestToZero (numbers) {
    if (!numbers.length) {
      return 0
    }

    let closest = 0

    for (let i = 0; i < numbers.length; i++) {
      if (closest === 0) {
        closest = numbers[i]
      } else if (numbers[i] === 0) {
        closest = numbers[i]
      } else if (numbers[i] > 0 && numbers[i] <= Math.abs(closest)) {
        closest = numbers[i]
      } else if (numbers[i] < 0 && -numbers[i] < Math.abs(closest)) {
        closest = numbers[i]
      }
    }

    return closest
  }

  currentCycle () {
    const cycles =
      this.viewport === 'mobile' ? this.mobileCycles : this.desktopCycles
    return cycles.find(cycle => cycle.active)
  }

  nextCycle () {
    const cycles =
      this.viewport === 'mobile' ? this.mobileCycles : this.desktopCycles
    const currentIndex = cycles.findIndex(cycle => cycle.active)

    let nextIndex = currentIndex
    do {
      nextIndex = (nextIndex + 1) % cycles.length
    } while (
      this.hasFilterParams &&
      !this.mergeLifestyleBlocks &&
      cycles[nextIndex].lifestyle === true &&
      nextIndex !== currentIndex
    )

    return cycles[nextIndex]
  }

  async switchToGrid () {
    window.scrollTo(0, 0)

    this.editorialViewActive = false

    await this.loadGrid()
  }

  async switchToEditorial () {
    window.scrollTo(0, 0)

    this.editorialViewActive = true

    await this.loadEditorial()
  }

  async loadEditorial () {
    // Cache the DOM so we don't fetch every cycle.
    if (!this.newEditorialDOM) {
      // Leave the section ID out, since we can't always know it.
      const fetchSection = `&section=`
      const fetchContext = `/collections/${this.collectionHandle}?view=${this.gridSwitcherTemplateName}`

      const section = await fetch(`${fetchContext}${fetchSection}`)
      const HTMLText = await section.text()
      this.newEditorialDOM = window.BAO.sectionRenderer.convertToDOM(HTMLText)
    }

    const editorialEl = this.newEditorialDOM.querySelector(
      '[data-editorial-view-section]',
    )

    if (!editorialEl) {
      // eslint-disable-next-line no-console
      console.warn(
        'The editorial view could not be found. Please make sure the template name is correct.',
      )
      return
    }

    this.externalEls.section.element.outerHTML = editorialEl.outerHTML
  }

  async loadGrid () {
    if (!this.gridSwitcherTemplateName) {
      // eslint-disable-next-line no-console
      console.log(
        'No grid template name was found. Please check collection.metafields.editorial_view.template_name',
      )
      return
    }

    // Get filter params from the URL.
    const splitUrl = window.location.search.split('?')
    const urlParams = splitUrl && splitUrl[1] ? `&${splitUrl[1]}` : ''

    if (!this.newGridDOM) {
      // Leave the section ID out, since we can't always know it. Load the whole
      // page and grab the section by a data attribute.
      const fetchSection = `&section=`
      const fetchContext = `/collections/${this.collectionHandle}?view=collection${this.gridPageNumber}${urlParams}`

      const section = await fetch(`${fetchContext}${fetchSection}`)
      const HTMLText = await section.text()

      this.newGridDOM = window.BAO.sectionRenderer.convertToDOM(HTMLText)
    }

    const gridEl = this.newGridDOM.querySelector('[data-grid-view-section]')
    this.externalEls.section.element.outerHTML = gridEl.outerHTML
  }

  // This is a stand-in for the filter reset button in editorial mode.
  // Should be an edge case, will happen when an editorial PLP is loaded with filter params.
  onFilterResetClick (e) {
    e.stopImmediatePropagation()

    this.updateURLHash(true)

    // Reload the page with no filters.
    location.reload(true)
  }

  onFilterButtonClick () {
    this.listeners.add(document, 'click', this.filterOptionClick)
  }

  filterOptionClick (e) {
    const filterOption = e.target.closest('[data-filter-option]')

    // If click came from inside the collection filters drawer, change to grid and apply filters.
    if (filterOption) {
      e.preventDefault()
      e.stopImmediatePropagation()

      // Update URL with filter params.
      this.updateURLHash()

      // Swap to grid mode, this will use the new filters.
      this.cycleColumns()

      this.listeners.remove('click', this.filterOptionClick)
    }
  }

  updateButtons (buttonEl) {
    const buttonViewport = buttonEl.dataset.viewport
    const buttonEls = this.els.button.elements

    buttonEls.forEach(el => {
      if (el.dataset.viewport !== buttonViewport) return

      const isActive = el === buttonEl

      el.dataset.active = isActive ? 'true' : 'false'
    })
  }

  updateURLHash (clear = false) {
    let searchParams = ''

    if (!clear) {
      const filterForm = getElement('form[is="collection-filters-form"]')
      const formData = new FormData(filterForm)
      // eslint-disable-next-line compat/compat
      searchParams = new URLSearchParams(formData).toString()
    }

    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  buildMobileCycles () {
    const cycles = []
    const merged = this.mergeLifestyleBlocks

    if (this.lifestyleViewEnabled && !this.hidePromos) {
      if (!this.hasFilterParams || merged) {
        cycles.push({
          columns: 2,
          lifestyle: true,
          editorial: false,
          active: merged
            ? !this.editorialViewActive && this.columnsMobile === 2
            : !this.editorialViewActive &&
              !this.hideLifestyleMobile &&
              this.columnsMobile === 2,
        })
      }
    }

    if (!merged) {
      cycles.push({
        columns: 2,
        lifestyle: false,
        editorial: false,
        active:
          !this.editorialViewActive &&
          (this.hideLifestyleMobile ||
            this.hasFilterParams ||
            !this.lifestyleViewEnabled) &&
          this.columnsMobile === 2,
      })
    }

    cycles.push({
      columns: 1,
      lifestyle: false,
      editorial: false,
      active: !this.editorialViewActive && this.columnsMobile === 1,
    })

    if (this.editorialViewEnabled && !this.hidePromos) {
      cycles.push({
        columns: null,
        lifestyle: false,
        editorial: true,
        active: this.editorialViewActive,
      })
    }

    return cycles
  }

  buildDesktopCycles () {
    const cycles = []
    const merged = this.mergeLifestyleBlocks

    if (this.lifestyleViewEnabled && !this.hidePromos) {
      if (!this.hasFilterParams || merged) {
        cycles.push({
          columns: 4,
          lifestyle: true,
          editorial: false,
          active: merged
            ? !this.editorialViewActive && this.columnsDesktop === 4
            : !this.editorialViewActive &&
              !this.hideLifestyleDesktop &&
              this.columnsDesktop === 4,
        })
      }
    }

    if (!merged) {
      cycles.push({
        columns: 4,
        lifestyle: false,
        editorial: false,
        active:
          !this.editorialViewActive &&
          (this.hideLifestyleDesktop ||
            this.hasFilterParams ||
            !this.lifestyleViewEnabled) &&
          this.columnsDesktop === 4,
      })
    }

    cycles.push({
      columns: 6,
      lifestyle: false,
      editorial: false,
      active: !this.editorialViewActive && this.columnsDesktop === 6,
    })

    if (this.editorialViewEnabled && !this.hidePromos) {
      cycles.push({
        columns: null,
        lifestyle: false,
        editorial: true,
        active: this.editorialViewActive,
      })
    }

    return cycles
  }

  updateCycles () {
    this._mobileCycles = this.buildMobileCycles()
    this._desktopCycles = this.buildDesktopCycles()
  }

  setLoading () {
    this.loading = true

    this.externalEls.section.element.setAttribute('aria-busy', 'true')

    this.els.next?.element?.setAttribute('disabled', '')
  }

  unsetLoading () {
    this.loading = false

    this.externalEls.section.element.setAttribute('aria-busy', 'false')

    this.els.next?.element?.removeAttribute('disabled')
  }

  get hasFilterParams () {
    const params = new URLSearchParams(window.location.search)
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) {
        return true
      }
    }
    return false
  }

  get storageNameColumnsDesktop () {
    return `grid_columns_desktop`
  }

  get storageNameColumnsMobile () {
    return `grid_columns_mobile`
  }

  get storageNameHideLifestyleMobile () {
    return `grid_hide_lifestyle_mobile`
  }

  get storageNameHideLifestyleDesktop () {
    return `grid_hide_lifestyle_desktop`
  }

  get prdListElem () {
    return getElement('[data-prd-list-clc]')
  }

  get mobileCycles () {
    return this._mobileCycles
  }

  get desktopCycles () {
    return this._desktopCycles
  }

  get editorialViewEnabled () {
    return this.dataset.editorialViewEnabled === 'true'
  }

  get hidePromos () {
    return this.externalEls.section?.element?.hasAttribute('data-hide-promos')
  }

  get lifestyleViewEnabled () {
    return this.dataset.lifestyleViewEnabled === 'true'
  }

  get mergeLifestyleBlocks () {
    return (
      this.dataset.mergeLifestyleBlocks === 'true' && this.lifestyleViewEnabled
    )
  }

  get gridSwitcherTemplateName () {
    return this.dataset.gridSwitcherTemplateName
  }

  get collectionHandle () {
    return this.dataset.collectionHandle
  }

  get gridPageNumber () {
    // eslint-disable-next-line compat/compat
    const urlParams = new URLSearchParams(window.location.search)
    const pageNumber = urlParams.get('page')
    let pageQuery = ''

    if (pageNumber) {
      pageQuery = `&page=${pageNumber}`
    }

    return pageQuery
  }
}

if (!customElements.get('grid-switcher')) {
  customElements.define('grid-switcher', GridSwitcher)
}
